import { generatePath } from 'react-router-dom';
import { useModal } from 'src/hooks/modalHooks';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Delete } from '@mui/icons-material';
import { pepApiRequest, RequestMethod } from 'src/services';
import {
  DataTableActionsButton,
  useRowClickHandler,
} from 'src/components/DataTable';
import { isModalComplete } from 'src/components/ModalDialog';
import { useUserRoles } from 'src/auth';
import { SolutionAssetConfigListItem } from '../../types';
import { API_DOWNLOAD_ASSET_MODEL } from '../../constants';
import SolutionAssetModelDeleteModal from './SolutionAssetModelDeleteModal';
import { useSolutionUserRoles } from '../../hooks';

type Props = {
  item?: SolutionAssetConfigListItem;
  solutionId?: string;
  twinId?: string;
};

function SolutionAssetModelAction({ item, solutionId, twinId }: Props) {
  const { isPlatformReader } = useUserRoles();
  const { isValidSolutionOwner } = useSolutionUserRoles(solutionId);
  const onClose = (event, reason) => isModalComplete(reason);

  const [DeleteDialog, handleDeleteClick] = useModal(SolutionAssetModelDeleteModal, {
    item,
    twinId,
    onClose,
  });

  const handleDownload = async () => {
    if (!item || !solutionId || !twinId) {
      return;
    }

    try {
      const downloadUrl = generatePath(API_DOWNLOAD_ASSET_MODEL, {
        solutionId,
        twinId,
        fileId: item.fileId,
      });

      const response = await pepApiRequest({
        method: RequestMethod.Get,
        url: downloadUrl,
        useProxy: false
      });

      if (response instanceof Response) {
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = item.fileName || 'asset-file';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  return (
    <>
      <DataTableActionsButton
        data-testid="solution-asset-config-files-view-action"
        icon={FileDownloadIcon}
        disabled={!item || !solutionId || !twinId || isPlatformReader}
        title="Download"
        onClick={handleDownload}
      />
      <DataTableActionsButton
        data-testid="solution-asset-config-files-delete-action"
        icon={Delete}
        disabled={!item || !solutionId || !twinId || !isValidSolutionOwner}
        title="Delete"
        onClick={handleDeleteClick}
      />
      {DeleteDialog}
    </>
  );
}

export default SolutionAssetModelAction;
